import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { AnimatePresence, motion } from 'framer-motion';
import { useContext, useEffect, useRef } from 'react';
import { Portal } from 'react-portal';

import { TranslationHash } from '~/components/translation/use-translations';
import { TimingObject } from '~/utils/count-down';

import UIContext from '~/ui/ui-context';
import { WidgetData } from './utils';
import VerticalCard from './vertical-card';
import { Backdrop } from './widget-styles';

const slideIn = {
	hidden: {
		y: '100vh',
		opacity: 0
	},
	visible: {
		y: '0',
		opacity: 1,
		transition: {
			duration: 0.3,
			ease: 'easeOut'
		}
	},
	exit: {
		y: '100vh',
		opacity: 0
	}
};

type Props = {
	open: boolean;
	toggleShowModal: () => void;
	gameName: string;
	timingObject: TimingObject;
	translations: TranslationHash;
} & WidgetData;

const CardModal = ({ open, timingObject, toggleShowModal, ...widget }: Props) => {
	const modalRef = useRef<HTMLDivElement>(null);
	const { hasStaticHeader } = useContext(UIContext);

	useEffect(() => {
		if (modalRef.current) {
			if (open) {
				disableBodyScroll(modalRef.current);
			} else {
				enableBodyScroll(modalRef.current);
			}
		}
		return () => clearAllBodyScrollLocks();
	}, [modalRef, open]);

	useEffect(() => {
		if (timingObject.completed && open) {
			toggleShowModal();
		}
	}, [open, timingObject.completed, toggleShowModal]);

	return (
		<Portal>
			<AnimatePresence mode="wait">
				{open && (
					<Backdrop
						onClick={toggleShowModal}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						hasStaticHeader={hasStaticHeader}
					>
						<motion.div
							onClick={e => e.stopPropagation()}
							variants={slideIn}
							initial="hidden"
							animate="visible"
							exit="exit"
						>
							<VerticalCard
								ref={modalRef}
								timingObject={timingObject}
								toggleShowModal={toggleShowModal}
								{...widget}
							/>
						</motion.div>
					</Backdrop>
				)}
			</AnimatePresence>
		</Portal>
	);
};

export default CardModal;
